import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useBeforeUnload, useBlocker, useLocation } from 'react-router-dom';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { BarChart3, BrainCircuit, Info, Code, Cog, Laptop2, Mic2, Volume2, AlertCircle, Settings2, Copy, CheckCircle2 } from 'lucide-react';
import { validateAssistantConfiguration } from '@/utils/voiceTranscriberCompatibility';
import { AnimatePresence } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InformationTab } from "./components/tabs/InformationTab";
import { BehaviorTab } from "./components/tabs";
import { TranscriberTab } from "./components/tabs";
import { VoiceTab } from "./components/tabs";
import { FunctionsTab } from './components/tabs';
import { AdvancedTab } from './components/tabs';
import { AnalysisTab } from './components/tabs';
import { AssistantHeader } from './components/AssistantHeader';
import { AssistantConfigSkeleton } from './components/AssistantConfigSkeleton';

import type { Assistant } from '@/api/assistants/assistants.types';
import { defaultAssistantConfig } from '@/api/assistants/defaultConfig';
import { createAssistant, getAssistantById, updateAssistant } from '@/api/assistants/assistants.api';
import { MobileOverlay } from '@/components/ui/mobile-overlay';
import { motion } from 'framer-motion';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Max name length is 40 characters as per backend requirement
const MAX_NAME_LENGTH = 40;
const CUSTOM_MODE_EMPTY_URL_PLACEHOLDER = '__CUSTOM_SOUND_URL_PENDING__'; // Added for validation

// Helper function for URL validation (copied from VoiceTab.tsx)
const isValidHttpUrl = (string: string): boolean => {
  // Allow empty string as it will be caught by CUSTOM_MODE_EMPTY_URL_PLACEHOLDER check
  if (!string) return true; 
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};



export function AssistantConfig() {
    const navigate = useNavigate();
    const { assistantId } = useParams();


    const { success, error } = useEnhancedToast();
    const location = useLocation();

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');

    const [config, setConfig] = useState<Omit<Assistant, 'name'>>(defaultAssistantConfig as Omit<Assistant, 'name'>);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(!!assistantId);
    const [key, setKey] = useState(0);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [bypassBlocker, setBypassBlocker] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);
    
    useEffect(() => {
        const loadData = async () => {
            if (assistantId) {
                // Load existing assistant
                try {
                    setIsLoadingData(true);
                    const response = await getAssistantById(assistantId);
                    if (response.error) {
                        error({
                            icon: AlertCircle,
                            title: "Failed to Load Assistant",
                            description: response.error,
                            duration: 4000,
                        });
                        navigate('/dashboard/assistants');
                    } else if (response.data) {
                        const assistant = response.data;
                        setName(assistant.name);
                        // Merge with default config to ensure all fields are present
                        setConfig({
                            ...defaultAssistantConfig,
                            ...assistant
                        });
                    }
                } catch (err) {
                    error({
                        icon: AlertCircle,
                        title: "Error Loading Assistant",
                        description: err instanceof Error ? err.message : "Failed to load assistant configuration",
                        duration: 4000,
                    });
                    navigate('/dashboard/assistants');
                } finally {
                    setIsLoadingData(false);
                }
            } else {
                // Check if we are navigating from a duplication action
                const duplicatedAssistantData = location.state?.duplicatedAssistantData as {
                    name: string;
                    config: Omit<Assistant, 'name'>;
                } | null;

                if (duplicatedAssistantData) {
                    // Initialize state from duplicated data
                    setName(duplicatedAssistantData.name);
                    setConfig(duplicatedAssistantData.config);
                    setIsLoadingData(false);
                } else {
                    // Reset all states for a brand new assistant (not duplicated)
                    setName('');
                    setConfig(defaultAssistantConfig as Omit<Assistant, 'name'>);
                    setIsLoadingData(false);
                }
            }
        };

        loadData();
    }, [assistantId, navigate, location.state]);

    // Track changes
    const handleConfigUpdate = (updates: Partial<Assistant>) => {
        setConfig(prev => {
            const newConfig = { ...prev, ...updates };
            setHasUnsavedChanges(true);
            return newConfig;
        });
    };

    // Handle name changes
    const handleNameChange = (newName: string) => {
        setName(newName);
        setHasUnsavedChanges(true);
    };

    // Handle config import
    const handleConfigImport = (newConfig: Omit<Assistant, 'name'>, newName: string) => {
        setConfig(newConfig);
        setName(newName);
        setHasUnsavedChanges(true);
        setKey(prev => prev + 1); // Force re-render to show imported data
    };

    // Prevent accidental navigation
    useBeforeUnload(
        useCallback(
            (event) => {
                if (hasUnsavedChanges) {
                    event.preventDefault();
                    return event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
                }
            },
            [hasUnsavedChanges]
        )
    );

    // Block navigation and show dialog
    const blocker = useBlocker(
        useCallback(
            ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }, nextLocation: { pathname: string } }) => {
                // Only block if we have unsaved changes and we're not already handling navigation
                return hasUnsavedChanges && 
                       currentLocation.pathname !== nextLocation.pathname &&
                       !showUnsavedDialog &&
                       !bypassBlocker &&
                       pendingAction === null;
            },
            [hasUnsavedChanges, showUnsavedDialog, bypassBlocker, pendingAction]
        )
    );

    // Update the handleCancel function
    const handleCancel = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        // Set a small timeout to prevent race conditions with the blocker
        setTimeout(() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
            setBypassBlocker(true);
            // Add another timeout to reset the bypass
            setTimeout(() => {
                setBypassBlocker(false);
            }, 100);
        }, 0);
    }, [blocker]);

    // Update the blocker effect
    useEffect(() => {
        if (blocker.state === "blocked" && !showUnsavedDialog) {
            setShowUnsavedDialog(true);
            setPendingAction(() => () => {
                setBypassBlocker(true);
                blocker.proceed();
            });
        }
    }, [blocker, showUnsavedDialog]);

    const handleBack = () => {
        if (hasUnsavedChanges && !showUnsavedDialog) {
            setShowUnsavedDialog(true);
            setPendingAction(() => () => {
                setBypassBlocker(true);
                navigate('/dashboard/assistants');
            });
        } else {
            navigate('/dashboard/assistants');
        }
    };

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Name is required');
            return;
        }
        
        if (trimmedName.length > MAX_NAME_LENGTH) {
            setNameError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
            return;
        }
        
        setNameError('');

        // Validation for custom background sound URL format
        if (config.backgroundSound && 
            config.backgroundSound !== 'off' && 
            config.backgroundSound !== 'office' && 
            config.backgroundSound !== CUSTOM_MODE_EMPTY_URL_PLACEHOLDER &&
            !isValidHttpUrl(config.backgroundSound)) {
            error({
                icon: AlertCircle,
                title: "Invalid Custom Sound URL",
                description: "Please provide a valid HTTP/HTTPS URL for the custom background sound, or clear the field.",
                duration: 4500,
            });
            return;
        }
        
        // Check for other validation errors (e.g., from AdvancedTab)
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            error({
                icon: AlertCircle,
                title: "Validation Error",
                description: "Please fix the validation errors before saving",
                duration: 4000,
            });
            return;
        }

        // Validate voice-transcriber compatibility
        const compatibilityCheck = validateAssistantConfiguration(config);
        if (!compatibilityCheck.isCompatible) {
            error({
                icon: AlertCircle,
                title: "Voice-Transcriber Compatibility Error",
                description: compatibilityCheck.message || "The selected voice and transcriber language are not compatible",
                duration: 6000,
            });
            return;
        }
        
        setIsLoading(true);

        // Set these before making the API call
        setBypassBlocker(true);
        setHasUnsavedChanges(false);

        try {
            // Remove server field if it's empty
            const { server, ...cleanConfig } = config;
            const hasServerContent = server && typeof server === 'object' && Object.keys(server).length > 0;
            
            const payload = {
                ...cleanConfig,
                name: trimmedName,
                ...(hasServerContent ? { server } : {})
            };
            

            const response = assistantId 
                ? await updateAssistant(assistantId, payload)
                : await createAssistant(payload);

            if (response.error) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Save",
                    description: response.error,
                    duration: 4000,
                });
            } else if (response.data) {
                const { name: newName, ...newConfig } = response.data;
                setName(newName);
                setConfig(newConfig);

                if (assistantId) {
                    success({
                        icon: Settings2,
                        title: "Assistant Updated",
                        description: "Your assistant configuration has been successfully updated",
                        duration: 3000,
                    });
                } else {
                    success({
                        icon: CheckCircle2,
                        title: "Assistant Created",
                        description: "Your new AI assistant has been created and is ready to use",
                        duration: 4000
                    });
                    navigate(`/dashboard/assistants/${response.data.id}`);
                }
            }
        } catch (err: unknown) {
            error({
                icon: AlertCircle,
                title: "Save Failed",
                description: err instanceof Error ? err.message : "Failed to save assistant configuration",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
            setBypassBlocker(false); // Reset the bypass
        }
    };

    const handleDuplicate = async () => {
        const proceedWithDuplicate = async () => {
            setIsDuplicating(true);
            try {
                // Create a copy suffix that's short but meaningful
                const copyName = name.trim();
                const suffix = " c1";
                let newName = '';
                
                // Check if we need to truncate the name before adding the copy suffix
                if (copyName.length + suffix.length > MAX_NAME_LENGTH) {
                    // Ensure total length doesn't exceed MAX_NAME_LENGTH
                    newName = copyName.substring(0, MAX_NAME_LENGTH - suffix.length) + suffix;
                } else {
                    newName = copyName + suffix;
                }
                
                // Prepare the duplicated data with new name
                const duplicatedAssistantData = {
                    name: newName,
                    config: config
                };
                
                setBypassBlocker(true);
                
                // Navigate to the 'new' page, passing the data in state
                navigate('/dashboard/assistants/new', {
                    replace: true,
                    state: { duplicatedAssistantData }
                });
                setKey(prev => prev + 1);

                success({
                    icon: Copy,
                    title: "Assistant Duplicated",
                    description: "Configuration has been copied. You can now customize your new assistant.",
                    duration: 4000
                });
            } catch (err) {
                error({
                    icon: AlertCircle,
                    title: "Duplication Failed",
                    description: err instanceof Error ? err.message : "Failed to duplicate assistant configuration",
                    duration: 4000
                });
            } finally {
                setIsDuplicating(false);
            }
        };

        if (hasUnsavedChanges && !showUnsavedDialog) {
            setShowUnsavedDialog(true);
            setPendingAction(() => proceedWithDuplicate);
        } else {
            await proceedWithDuplicate();
        }
    };

    useEffect(() => {
        if (bypassBlocker && !showUnsavedDialog && !pendingAction) {
            setBypassBlocker(false);
        }
    }, [location, showUnsavedDialog, pendingAction]);

    return (
        <div className="h-full flex flex-col">
            {/* Mobile Overlay */}
            <AnimatePresence>
                <MobileOverlay
                    icon={Laptop2}
                    title="Desktop View Only"
                    description="Assistant configuration is currently optimized for desktop view. Mobile support coming soon!"
                    onBack={() => navigate('/dashboard/assistants')}
                />
            </AnimatePresence>

            {/* Main Content */}
            <div className="hidden sm:flex flex-1 flex-col overflow-hidden">
                {isLoadingData ? (
                    <AssistantConfigSkeleton />
                ) : (
                    <AnimatePresence mode="wait">
                        <>
                            <AssistantHeader
                                name={name}
                                setName={handleNameChange}
                                nameError={nameError}
                                setNameError={setNameError}
                                onBack={handleBack}

                                assistantId={assistantId}
                                onSave={handleSave}
                                isLoading={isLoading}
                                isDuplicating={isDuplicating}
                                onDuplicate={handleDuplicate}
                                backgroundSound={config.backgroundSound}
                                hasUnsavedChanges={hasUnsavedChanges}
                                config={config}
                                onConfigUpdate={handleConfigImport}
                            />

                            <div className="flex-1 overflow-y-auto">
                                <motion.div 
                                    key={`assistant-config-${key}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                    className="px-4 py-6 md:px-8 md:py-8"
                                >
                                    <Tabs defaultValue="information" className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <TabsList className="h-11">
                                                <TabsTrigger value="information" className="gap-2 px-4">
                                                    <Info className="h-4 w-4" />
                                                    Information
                                                </TabsTrigger>
                                                <TabsTrigger value="behavior" className="gap-2 px-4">
                                                    <BrainCircuit className="h-4 w-4" />
                                                    Behavior
                                                </TabsTrigger>
                                                <TabsTrigger value="voice" className="gap-2 px-4">
                                                    <Volume2 className="h-4 w-4" />
                                                    Voice
                                                </TabsTrigger>
                                                <TabsTrigger value="transcriber" className="gap-2 px-4">
                                                    <Mic2 className="h-4 w-4" />
                                                    Transcriber
                                                </TabsTrigger>
                                                <TabsTrigger value="functions" className="gap-2 px-4">
                                                    <Code className="h-4 w-4" />
                                                    Functions
                                                </TabsTrigger>
                                                <TabsTrigger value="advanced" className="gap-2 px-4">
                                                    <Cog className="h-4 w-4" />
                                                    Advanced
                                                </TabsTrigger>
                                                <TabsTrigger value="analysis" className="gap-2 px-4">
                                                    <BarChart3 className="h-4 w-4" />
                                                    Analysis
                                                </TabsTrigger>
                                            </TabsList>

                                    
                                        </div>

                                        <TabsContent value="information" className="m-0">
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <InformationTab
                                                    name={name}
                                                    setName={handleNameChange}
                                                    nameError={nameError}
                                                    setNameError={setNameError}
                                                    assistantId={assistantId}
                                                    createdAt={config.createdAt}
                                                    updatedAt={config.updatedAt}
                                                />
                                            </motion.div>
                                        </TabsContent>

                                        <TabsContent value="behavior" className="m-0">
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <BehaviorTab
                                                    assistant={config}
                                                    onUpdate={handleConfigUpdate}
                                                />
                                            </motion.div>
                                        </TabsContent>

                                <TabsContent value="voice" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <VoiceTab
                                            assistant={config}
                                            onUpdate={handleConfigUpdate}
                                        />
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="transcriber" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <TranscriberTab
                                            assistant={config}
                                            onUpdate={handleConfigUpdate}
                                        />
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="functions" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <FunctionsTab
                                            assistant={config}
                                            onUpdate={handleConfigUpdate}
                                        />
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="advanced" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <AdvancedTab
                                            assistant={config}
                                            onUpdate={handleConfigUpdate}
                                            setValidationErrors={setValidationErrors}
                                        />
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="analysis" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <AnalysisTab
                                            assistant={config}
                                            onUpdate={handleConfigUpdate}
                                        />
                                    </motion.div>
                                </TabsContent>
                                    </Tabs>
                                </motion.div>
                            </div>
                        </>
                    </AnimatePresence>
                )}
            </div>

            <AlertDialog 
                open={showUnsavedDialog} 
                onOpenChange={(open) => {
                    if (!open) {
                        handleCancel();
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel 
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default to avoid double-triggering
                                handleCancel();
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default to avoid double-triggering
                                if (pendingAction) {
                                    setBypassBlocker(true);
                                    pendingAction();
                                    setHasUnsavedChanges(false);
                                    setShowUnsavedDialog(false);
                                    setPendingAction(null);
                                }
                            }}
                        >
                            Leave Without Saving
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default AssistantConfig;